import { BorderedContainer } from 'core/components/common/BorderedContainer';
import {
  convertToPresenceAbsence,
  formatDateTime,
} from 'core/components/common/converters';
import { SectionTitle } from 'core/components/common/SectionTitle';
import { InfoDisplay } from 'core/components/JianDetail/common/InfoDisplay';
import { IncidentDetailPatient } from 'sections/ube/features/IncidentDetail/useIncidentDetailForUbe';
import { TagList } from 'core/components/JianDetail/PatientDetailInfo/common/TagList';

type Props = {
  specialTreatment: IncidentDetailPatient['treatments'][number]['data'];
};

export const SpecialTreatmentView = ({ specialTreatment }: Props) => {
  if (!specialTreatment) {
    return (
      <BorderedContainer>
        <div className="flex flex-col gap-[24px]">
          <SectionTitle text="特定行為" />
          <p>データが存在しません</p>
        </div>
      </BorderedContainer>
    );
  }

  return (
    <BorderedContainer>
      <div className="flex flex-col gap-[24px]">
        <SectionTitle text="特定行為" />

        <SectionTitle text="CPA" size="medium" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="CPA目撃"
            content={specialTreatment.cpaData?.cpaTiming}
          />
          <InfoDisplay
            title="記録時刻"
            content={formatDateTime(specialTreatment.cpaData?.recordedAt)}
          />
          <InfoDisplay
            title="確/推/不明"
            content={specialTreatment.cpaData?.confidenceType}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="BsCPR"
            content={specialTreatment.bscprData?.bscprTiming}
          />
          <InfoDisplay
            title="記録時刻"
            content={formatDateTime(specialTreatment.bscprData?.recordedAt)}
          />
          <InfoDisplay
            title="確/推/不明"
            content={specialTreatment.bscprData?.confidenceType}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="DC" content={specialTreatment.dcData?.dcTiming} />
          <InfoDisplay
            title="記録時刻"
            content={formatDateTime(specialTreatment.dcData?.recordedAt)}
          />
          <InfoDisplay
            title="着前"
            content={specialTreatment.dcData?.dcAttempts ?? ''}
          />
        </div>

        <SectionTitle text="特定行為" size="medium" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="特定行為同意"
            content={specialTreatment.agreedBy}
          />
          <InfoDisplay title="取得者" content={specialTreatment.executorName} />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="IC"
            content={formatDateTime(specialTreatment.informedConsentAt)}
          />
          <InfoDisplay
            title="要請"
            content={formatDateTime(specialTreatment.suggestedAt)}
          />
          <InfoDisplay
            title="取得"
            content={formatDateTime(
              specialTreatment.doctorInstructionReceivedAt
            )}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="病院"
            content={specialTreatment.instructingHospitalName}
          />
          <InfoDisplay
            title="医師"
            content={specialTreatment.instructingDoctorName}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="内容"
            content={<TagList tags={specialTreatment.executeTreatment ?? []} />}
          />
        </div>

        <SectionTitle text="初回ROSC" size="medium" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay title="初期波形" content={specialTreatment.initialEcg} />
          <InfoDisplay
            title="記録時間"
            content={formatDateTime(specialTreatment.initialEcgAt)}
          />
          <InfoDisplay
            title="ROSC(初回)"
            content={formatDateTime(specialTreatment.firstRoscRecordedAt)}
          />
        </div>

        <SectionTitle text="静脈路" size="medium" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="実施者"
            content={specialTreatment.ivAccessExecutorName}
          />
          <InfoDisplay
            title="静脈路適応"
            content={specialTreatment.ivAccessTiming}
          />
          <InfoDisplay
            title="実施"
            content={specialTreatment.ivAccessExecute}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="確保時間1st"
            content={formatDateTime(
              specialTreatment.ivAccessData?.[0]?.recordedAt
            )}
          />
          <InfoDisplay
            title="部位(左右)"
            content={specialTreatment.ivAccessData?.[0]?.ivAccessSide}
          />
          <InfoDisplay
            title="部位"
            content={specialTreatment.ivAccessData?.[0]?.ivAccessPart}
          />
          <InfoDisplay
            title="サイズ1st"
            content={specialTreatment.ivAccessData?.[0]?.ivAccessSyringeSize}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="確保時間2nd"
            content={formatDateTime(
              specialTreatment.ivAccessData?.[1]?.recordedAt
            )}
          />
          <InfoDisplay
            title="部位(左右)"
            content={specialTreatment.ivAccessData?.[1]?.ivAccessSide}
          />
          <InfoDisplay
            title="部位"
            content={specialTreatment.ivAccessData?.[1]?.ivAccessPart}
          />
          <InfoDisplay
            title="サイズ2nd"
            content={specialTreatment.ivAccessData?.[1]?.ivAccessSyringeSize}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="輸液速度"
            content={specialTreatment.infusionRate ?? ''}
          />
          <InfoDisplay
            title="輸液量"
            content={specialTreatment.infusionValue ?? ''}
          />
        </div>

        <div className="mt-[17px] mb-[5px]">
          <SectionTitle text="気道確保" size="medium" />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="実施者"
            content={specialTreatment.airwayManagementExecutorName}
          />
          <InfoDisplay
            title="気道確保1st(開始)"
            content={formatDateTime(
              specialTreatment.airwayManagementData?.startedAt
            )}
          />
          <InfoDisplay
            title="気道確保2nd(終了)"
            content={formatDateTime(
              specialTreatment.airwayManagementData?.completionAt
            )}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="種類"
            content={specialTreatment.airwayManagementData?.deviceType}
          />
          <InfoDisplay
            title="喉頭展開"
            content={specialTreatment.airwayManagementData?.laryngoscopy}
          />
          <InfoDisplay
            title="Cormack Grade"
            content={specialTreatment.airwayManagementData?.cormackGrade ?? ''}
          />
          <InfoDisplay
            title="チューブサイズ"
            content={specialTreatment.airwayManagementData?.tubeSize ?? ''}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="固定"
            content={
              specialTreatment.airwayManagementData?.tubeFixationDepth ?? ''
            }
          />
          <InfoDisplay
            title="EtCO2"
            content={specialTreatment.airwayManagementData?.etCo2Value ?? ''}
          />
          <InfoDisplay
            title="カフ"
            content={specialTreatment.airwayManagementData?.cuffValueType}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="換気"
            content={specialTreatment.airwayManagementData?.ventilation}
          />
          <InfoDisplay
            title="確保"
            content={specialTreatment.airwayManagementExecute}
          />
        </div>

        <SectionTitle text="血糖値測定" size="medium" />
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="実施者1st"
            content={specialTreatment.gluMeasureData?.[0]?.executorName}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="血糖測定適応1st"
            content={convertToPresenceAbsence(
              specialTreatment.gluMeasureData?.[0]?.isMeasured
            )}
          />
          <InfoDisplay
            title="測定時刻"
            content={formatDateTime(
              specialTreatment.gluMeasureData?.[0]?.recordedAt
            )}
          />
          <InfoDisplay
            title="血糖値"
            content={specialTreatment.gluMeasureData?.[0]?.gluValue ?? ''}
          />
          <InfoDisplay
            title="測定"
            content={
              specialTreatment.gluMeasureData?.[0]?.isFailed ? 'できず' : ''
            }
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="実施者2nd"
            content={specialTreatment.gluMeasureData?.[1]?.executorName}
          />
        </div>
        <div className="flex flex-wrap gap-x-[40px] gap-y-[24px] xl:gap-x-[96px]">
          <InfoDisplay
            title="血糖測定適応2nd"
            content={convertToPresenceAbsence(
              specialTreatment.gluMeasureData?.[1]?.isMeasured
            )}
          />
          <InfoDisplay
            title="測定時刻"
            content={formatDateTime(
              specialTreatment.gluMeasureData?.[1]?.recordedAt
            )}
          />
          <InfoDisplay
            title="血糖値"
            content={specialTreatment.gluMeasureData?.[1]?.gluValue ?? ''}
          />
          <InfoDisplay
            title="測定"
            content={
              specialTreatment.gluMeasureData?.[1]?.isFailed ? 'できず' : ''
            }
          />
        </div>

        <SectionTitle text="備考" size="medium" />
        <div>
          <InfoDisplay
            title="備考"
            content={specialTreatment.specialTreatmentNote}
          />
        </div>
      </div>
    </BorderedContainer>
  );
};
